import { ModalControls, ModalType, showModal } from "components/Modal";
import TabbedButton, { TabbedButtonType } from "components/TabbedButton";
import { IconType } from "components/Icons";
import { UserPartner } from "modules/auth/UserProfile";
import React, { useMemo, useState } from "react";
import { navigateToSearchScreen } from "modules/navigation/helpers/navigator";
import { useDispatch } from "react-redux";

type PartnerEntryModalFormContentProps = {
  getSubmitHandle?: (submitHandle: () => Promise<void>) => void;
  initialPartnerId?: string;
  onPartnerChanged: (partnerId: string) => Promise<void> | void;
  controls: ModalControls;
  partners: Record<string, UserPartner>;
};

const PartnerEntryModalFormContent: React.FC<
  PartnerEntryModalFormContentProps
> = ({
  getSubmitHandle,
  initialPartnerId,
  onPartnerChanged,
  controls,
  partners,
}) => {
  const dispatch = useDispatch();
  const partnerIds = useMemo(
    () =>
      Object.keys(partners).sort((leftId, rightId) => {
        const left = partners[leftId]?.name || leftId;
        const right = partners[rightId]?.name || rightId;
        return left.localeCompare(right);
      }),
    [partners],
  );

  const [selectedPartnerId, setSelectedPartnerId] = useState<
    string | undefined
  >(initialPartnerId || partnerIds[0]);

  const handleSave = async () => {
    if (!selectedPartnerId) return;
    controls.busy(true);
    if (selectedPartnerId !== initialPartnerId) {
      await onPartnerChanged(selectedPartnerId);
    }
    controls.close();
  };

  getSubmitHandle && getSubmitHandle(handleSave);

  return (
    <div style={{ minWidth: 320 }}>
      <div style={{ marginBottom: 12 }}>
        Select which law firm should be active across the application.
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {partnerIds.map((partnerId) => {
          const name = partners[partnerId]?.name || partnerId;
          const isSelected = selectedPartnerId === partnerId;

          return (
            <label
              key={partnerId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="partner"
                checked={isSelected}
                onChange={() => setSelectedPartnerId(partnerId)}
              />
              <TabbedButton
                onClick={() => setSelectedPartnerId(partnerId)}
                style={
                  isSelected
                    ? TabbedButtonType.PRIMARY
                    : TabbedButtonType.SECONDARY
                }
                label={`${name} (${partnerId})`}
              />
            </label>
          );
        })}
        {/* New Law Firm button */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            paddingLeft: "18px",
          }}
        >
          <TabbedButton
            icon={IconType.PLUS}
            onClick={() => {
              controls.close();

              navigateToSearchScreen(dispatch);
            }}
            style={TabbedButtonType.SECONDARY}
            label="Join New Law Firm"
          />
        </div>
      </div>
    </div>
  );
};

export const showPartnerEntryModal = (
  onSave: (partnerId: string) => Promise<void> | void,
  partners: Record<string, UserPartner>,
  currentPartnerId?: string,
) => {
  let submitHandle: () => Promise<void>;
  const getSubmitHandle = (handle: () => Promise<void>) => {
    submitHandle = handle;
  };

  const handleFormSubmit = async () =>
    submitHandle ? submitHandle() : undefined;

  showModal({
    id: "partner_entry",
    title: "Select your law firm",
    type: ModalType.CONFIRM,
    cancellable: true,
    onConfirm: handleFormSubmit,
    renderContent: (controls: ModalControls) => (
      <PartnerEntryModalFormContent
        getSubmitHandle={getSubmitHandle}
        onPartnerChanged={onSave}
        initialPartnerId={currentPartnerId}
        controls={controls}
        partners={partners}
      />
    ),
  });
};
